import * as i0 from '@angular/core';
import { Component, Input, ViewChild, NgModule } from '@angular/core';
import { service, factories } from 'powerbi-client';

// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
/**
 * Get JSON string representation of the given map.
 *
 * @param map Map of event and corresponding handler method
 *
 */
const _c0 = ["reportContainer"];
const _c1 = ["embedConfig", ""];
const _c2 = ["dashboardContainer"];
const _c3 = ["tileContainer"];
const _c4 = ["paginatedReportContainer"];
const _c5 = ["visualContainer"];
const _c6 = ["qnaContainer"];
const stringifyMap = map => {
  // Return empty string for empty/null map
  if (!map) {
    return '';
  }
  // Get entries of map as array
  const mapEntries = Array.from(map);
  // Return JSON string
  return JSON.stringify(mapEntries.map(mapEntry =>
  // Convert event handler method to a string containing its source code for comparison
  [mapEntry[0], mapEntry[1] ? mapEntry[1].toString() : '']));
};
// SDK information to be used with service instance
const sdkType = 'powerbi-client-angular';
const sdkWrapperVersion = '3.0.5';

// Copyright (c) Microsoft Corporation.
/**
 * Base component to hold common properties for all the Power BI entities
 */
let PowerBIEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIEmbedComponent {
    constructor() {
      // JSON stringify of prev event handler map
      this.prevEventHandlerMapString = '';
    }
    ngOnInit() {
      // Initialize powerbi variable for child component
      if (this.service) {
        this.powerbi = this.service;
      } else {
        this.powerbi = new service.Service(factories.hpmFactory, factories.wpmpFactory, factories.routerFactory);
      }
      this.powerbi.setSdkInfo(sdkType, sdkWrapperVersion);
    }
    /**
     * Sets all event handlers from the input on the embedded entity
     *
     * @param embed Embedded object
     * @param eventHandlerMap Array of event handlers to be set on embedded entity
     * @returns void
     */
    setEventHandlers(embed, eventHandlerMap) {
      // Get string representation of eventHandlerMap
      const eventHandlerMapString = stringifyMap(eventHandlerMap);
      // Check if event handler map changed
      if (this.prevEventHandlerMapString === eventHandlerMapString) {
        return;
      }
      // Update prev string representation of event handler map
      this.prevEventHandlerMapString = eventHandlerMapString;
      // Apply all provided event handlers
      eventHandlerMap.forEach((eventHandlerMethod, eventName) => {
        // Removes event handler for this event
        embed.off(eventName);
        // Event handler is effectively removed for this event when eventHandlerMethod is null
        if (eventHandlerMethod) {
          // Set single event handler
          embed.on(eventName, event => {
            eventHandlerMethod(event, embed);
          });
        }
      });
    }
  }
  PowerBIEmbedComponent.ɵfac = function PowerBIEmbedComponent_Factory(t) {
    return new (t || PowerBIEmbedComponent)();
  };
  PowerBIEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIEmbedComponent,
    selectors: [["powerbi-embed"]],
    inputs: {
      cssClassName: "cssClassName",
      service: "service"
    },
    decls: 0,
    vars: 0,
    template: function PowerBIEmbedComponent_Template(rf, ctx) {},
    encapsulation: 2
  });
  return PowerBIEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Report component to embed the report, extends the Base Component
 */
let PowerBIReportEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIReportEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
      // Phased embedding flag (Optional)
      this.phasedEmbedding = false;
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    // Returns embed object to calling function
    getReport() {
      return this._embed;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateReport function
        this.embedOrUpdateReport(prevEmbedConfig);
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed, load or bootstrap
        if (this.embedConfig.accessToken && this.embedConfig.embedUrl) {
          this.embedReport();
        } else {
          this.embed = this.powerbi.bootstrap(this.containerRef.nativeElement, this.embedConfig);
        }
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    /**
     * Embed or load the PowerBI Report based on phasedEmbedding flag
     *
     * @returns void
     */
    embedReport() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      // Load when phasedEmbedding flag is true, embed otherwise
      if (this.phasedEmbedding) {
        this.embed = this.powerbi.load(this.containerRef.nativeElement, this.embedConfig);
      } else {
        this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
      }
    }
    /**
     * When component updates, choose to _embed_ or _load_ the powerbi report
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig IReportEmbedConfiguration
     * @returns void
     */
    embedOrUpdateReport(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed or load in the following scenario
      // Embed URL is updated (E.g. New report is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedReport();
      }
    }
  }
  PowerBIReportEmbedComponent.ɵfac = function PowerBIReportEmbedComponent_Factory(t) {
    return new (t || PowerBIReportEmbedComponent)();
  };
  PowerBIReportEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIReportEmbedComponent,
    selectors: [["powerbi-report", "embedConfig", ""]],
    viewQuery: function PowerBIReportEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c0, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig",
      phasedEmbedding: "phasedEmbedding",
      eventHandlers: "eventHandlers"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["reportContainer", ""]],
    template: function PowerBIReportEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBIReportEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Dashboard component to embed the dashboard, extends the Base component
 */
let PowerBIDashboardEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIDashboardEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    // Returns embed object to calling function
    getDashboard() {
      return this.embed;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateDashboard function
        this.embedOrUpdateDashboard(prevEmbedConfig);
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed or bootstrap
        if (this.embedConfig.accessToken && this.embedConfig.embedUrl) {
          this.embedDashboard();
        } else {
          this.embed = this.powerbi.bootstrap(this.containerRef.nativeElement, this.embedConfig);
        }
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    /**
     * Embed the PowerBI Dashboard
     *
     * @returns void
     */
    embedDashboard() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
    }
    /**
     * When component updates, choose to _embed_ the powerbi dashboard
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig IDashboardEmbedConfiguration
     * @returns void
     */
    embedOrUpdateDashboard(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed in the following scenario
      // Embed URL is updated (E.g. New dashboard is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedDashboard();
      }
    }
  }
  PowerBIDashboardEmbedComponent.ɵfac = function PowerBIDashboardEmbedComponent_Factory(t) {
    return new (t || PowerBIDashboardEmbedComponent)();
  };
  PowerBIDashboardEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIDashboardEmbedComponent,
    selectors: [["powerbi-dashboard", "embedConfig", ""]],
    viewQuery: function PowerBIDashboardEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c2, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig",
      eventHandlers: "eventHandlers"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["dashboardContainer", ""]],
    template: function PowerBIDashboardEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBIDashboardEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Tile component to embed the tile, extends Base component
 */
let PowerBITileEmbedComponent = /*#__PURE__*/(() => {
  class PowerBITileEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    // Returns embed object to calling function
    getTile() {
      return this._embed;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateDashboard function
        this.embedOrUpdateTile(prevEmbedConfig);
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed or bootstrap
        if (this.embedConfig.accessToken && this.embedConfig.embedUrl) {
          this.embedTile();
        } else {
          this.embed = this.powerbi.bootstrap(this.containerRef.nativeElement, this.embedConfig);
        }
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    /**
     * Embed the PowerBI Tile
     *
     * @returns void
     */
    embedTile() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
    }
    /**
     * When component updates, choose to _embed_ the powerbi tile
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig ITileEmbedConfiguration
     * @returns void
     */
    embedOrUpdateTile(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed in the following scenario
      // Embed URL is updated (E.g. New tile is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedTile();
      }
    }
  }
  PowerBITileEmbedComponent.ɵfac = function PowerBITileEmbedComponent_Factory(t) {
    return new (t || PowerBITileEmbedComponent)();
  };
  PowerBITileEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBITileEmbedComponent,
    selectors: [["powerbi-tile", "embedConfig", ""]],
    viewQuery: function PowerBITileEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c3, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig",
      eventHandlers: "eventHandlers"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["tileContainer", ""]],
    template: function PowerBITileEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBITileEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Paginated report component to embed the entity, extends the Base component
 */
let PowerBIPaginatedReportEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIPaginatedReportEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateDashboard function
        this.embedOrUpdatedPaginatedReport(prevEmbedConfig);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed
        this.embedPaginatedReport();
      }
    }
    /**
     * Embed the PowerBI Paginated report
     *
     * @returns void
     */
    embedPaginatedReport() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      // Embed paginated report
      this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
    }
    /**
     * When component updates, choose to _embed_ the powerbi paginated report
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig IEmbedConfiguration
     * @returns void
     */
    embedOrUpdatedPaginatedReport(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed in the following scenario
      // Embed URL is updated (E.g. New paginated report is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedPaginatedReport();
      }
    }
  }
  PowerBIPaginatedReportEmbedComponent.ɵfac = function PowerBIPaginatedReportEmbedComponent_Factory(t) {
    return new (t || PowerBIPaginatedReportEmbedComponent)();
  };
  PowerBIPaginatedReportEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIPaginatedReportEmbedComponent,
    selectors: [["powerbi-paginated-report", "embedConfig", ""]],
    viewQuery: function PowerBIPaginatedReportEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c4, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["paginatedReportContainer", ""]],
    template: function PowerBIPaginatedReportEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBIPaginatedReportEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Visual component to embed the visual, extends Base component
 */
let PowerBIVisualEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIVisualEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    // Returns embed object to calling function
    getVisual() {
      return this._embed;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateDashboard function
        this.embedOrUpdateVisual(prevEmbedConfig);
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed or bootstrap
        if (this.embedConfig.accessToken && this.embedConfig.embedUrl) {
          this.embedVisual();
        } else {
          this.embed = this.powerbi.bootstrap(this.containerRef.nativeElement, this.embedConfig);
        }
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    /**
     * Embed the PowerBI Visual
     *
     * @returns void
     */
    embedVisual() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
    }
    /**
     * When component updates, choose to _embed_ the powerbi visual
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig IVisualEmbedConfiguration
     * @returns void
     */
    embedOrUpdateVisual(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed in the following scenario
      // Embed URL is updated (E.g. New visual is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedVisual();
      }
    }
  }
  PowerBIVisualEmbedComponent.ɵfac = function PowerBIVisualEmbedComponent_Factory(t) {
    return new (t || PowerBIVisualEmbedComponent)();
  };
  PowerBIVisualEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIVisualEmbedComponent,
    selectors: [["powerbi-visual", "embedConfig", ""]],
    viewQuery: function PowerBIVisualEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c5, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig",
      eventHandlers: "eventHandlers"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["visualContainer", ""]],
    template: function PowerBIVisualEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBIVisualEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
/**
 * Qna component to embed the Qna visual, extends Base component
 */
let PowerBIQnaEmbedComponent = /*#__PURE__*/(() => {
  class PowerBIQnaEmbedComponent extends PowerBIEmbedComponent {
    constructor() {
      super();
    }
    // Getter for this._embed
    get embed() {
      return this._embed;
    }
    // Setter for this._embed
    set embed(newEmbedInstance) {
      this._embed = newEmbedInstance;
    }
    // Returns embed object to calling function
    getQna() {
      return this._embed;
    }
    ngOnInit() {
      // Initialize PowerBI service instance variable from parent
      super.ngOnInit();
    }
    ngOnChanges(changes) {
      if (changes.embedConfig) {
        const prevEmbedConfig = changes.embedConfig.previousValue;
        // Check if the function is being called for the first time
        if (!prevEmbedConfig) {
          return;
        }
        // Input from parent get updated, thus call embedOrUpdateDashboard function
        this.embedOrUpdateQna(prevEmbedConfig);
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    ngAfterViewInit() {
      // Check if container exists on the UI
      if (this.containerRef.nativeElement) {
        // Decide to embed or bootstrap
        if (this.embedConfig.accessToken && this.embedConfig.embedUrl) {
          this.embedQnaVisual();
        } else {
          this.embed = this.powerbi.bootstrap(this.containerRef.nativeElement, this.embedConfig);
        }
      }
      // Set event handlers if available
      if (this.eventHandlers && this.embed) {
        super.setEventHandlers(this.embed, this.eventHandlers);
      }
    }
    /**
     * Embed the PowerBI QnA Visual
     *
     * @returns void
     */
    embedQnaVisual() {
      // Check if the HTML container is rendered and available
      if (!this.containerRef.nativeElement) {
        return;
      }
      this.embed = this.powerbi.embed(this.containerRef.nativeElement, this.embedConfig);
    }
    /**
     * When component updates, choose to _embed_ the powerbi qna visual
     * or do nothing if the embedUrl and accessToken did not update in the new properties
     *
     * @param prevEmbedConfig IQnaEmbedConfiguration
     * @returns void
     */
    embedOrUpdateQna(prevEmbedConfig) {
      // Check if Embed URL and Access Token are present in current properties
      if (!this.embedConfig.accessToken || !this.embedConfig.embedUrl) {
        return;
      }
      // Embed in the following scenario
      // Embed URL is updated (E.g. New Qna visual is to be embedded)
      if (this.containerRef.nativeElement && this.embedConfig.embedUrl !== prevEmbedConfig.embedUrl) {
        this.embedQnaVisual();
      }
    }
  }
  PowerBIQnaEmbedComponent.ɵfac = function PowerBIQnaEmbedComponent_Factory(t) {
    return new (t || PowerBIQnaEmbedComponent)();
  };
  PowerBIQnaEmbedComponent.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: PowerBIQnaEmbedComponent,
    selectors: [["powerbi-qna", "embedConfig", ""]],
    viewQuery: function PowerBIQnaEmbedComponent_Query(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵviewQuery(_c6, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.containerRef = _t.first);
      }
    },
    inputs: {
      embedConfig: "embedConfig",
      eventHandlers: "eventHandlers"
    },
    features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature],
    attrs: _c1,
    decls: 2,
    vars: 3,
    consts: [["qnaContainer", ""]],
    template: function PowerBIQnaEmbedComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵelement(0, "div", null, 0);
      }
      if (rf & 2) {
        i0.ɵɵclassMap(ctx.cssClassName);
      }
    },
    encapsulation: 2
  });
  return PowerBIQnaEmbedComponent;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.
let PowerBIEmbedModule = /*#__PURE__*/(() => {
  class PowerBIEmbedModule {}
  PowerBIEmbedModule.ɵfac = function PowerBIEmbedModule_Factory(t) {
    return new (t || PowerBIEmbedModule)();
  };
  PowerBIEmbedModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: PowerBIEmbedModule
  });
  PowerBIEmbedModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    imports: [[]]
  });
  return PowerBIEmbedModule;
})();
(function () {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

// Copyright (c) Microsoft Corporation.

/**
 * Generated bundle index. Do not edit.
 */

export { PowerBIDashboardEmbedComponent, PowerBIEmbedModule, PowerBIPaginatedReportEmbedComponent, PowerBIQnaEmbedComponent, PowerBIReportEmbedComponent, PowerBITileEmbedComponent, PowerBIVisualEmbedComponent };
